import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ISpaceTemplate } from '../../models/session';
import { User } from '../../models/user';
import { AclService } from '../../services/acl.service';
import { UserService } from '../../services/user.service';
import { ThemeService } from '../../services/theme.service';
import { NotificationToasterService } from '../../services/notification-toaster.service';
import { SpacesHomePageView } from '../spaces-view/spaces-view.component';
import { IconTypes } from '../../standalones/components/pencil-icon/pencil-icon.component';
import { URL_CONSTANTS } from '../../common/utils/url';

@Component({
  selector: 'app-spaces-templates-list-item',
  templateUrl: './spaces-templates-list-item.component.html',
  styleUrls: ['./spaces-templates-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpacesTemplatesListItemComponent {
  private _spaceTemplate: ISpaceTemplate | undefined;
  private readonly spaceTemplate$ = new BehaviorSubject<ISpaceTemplate | undefined>(undefined);

  @Input() set spaceTemplate(value: ISpaceTemplate | undefined) {
    this._spaceTemplate = value;
    this.spaceTemplate$.next(value);
  }
  get spaceTemplate(): ISpaceTemplate | undefined {
    return this._spaceTemplate;
  }
  @Input() isBlankTemplate = false;
  @Input() currentPageView = SpacesHomePageView.SPACES_LIST_VIEW;
  private _currentUser: User | undefined;
  private readonly currentUser$ = new BehaviorSubject<User | undefined>(undefined);
  @Input() set currentUser(value: User | undefined) {
    this._currentUser = value;
    this.currentUser$.next(value);
    this.isInstitutionAdmin = !!value && !!value.institution && this.aclService.isAdmin(value);
  }
  get currentUser(): User | undefined {
    return this._currentUser;
  }

  @Output() handleTemplateClicked = new EventEmitter<ISpaceTemplate | undefined>();
  @Output() handleUpdateTemplateProps = new EventEmitter<ISpaceTemplate>();
  @Output() handleDeleteTemplateClicked = new EventEmitter<ISpaceTemplate>();

  isInstitutionAdmin = false;
  actionMenuOpened$ = new BehaviorSubject(false);
  protected readonly SpacesHomePageView = SpacesHomePageView;
  protected readonly IconTypes = IconTypes;

  readonly vm$ = combineLatest([
    this.currentUser$,
    this.spaceTemplate$,
    this.themeService.squareLogo$,
  ]).pipe(
    map(([currentUser, spaceTemplate, squareLogo]) => {
      const institution = currentUser?.institution;
      const logo = squareLogo;
      const hasLogo = !!logo;
      const isSameInstitution = spaceTemplate?.institution === institution?._id;
      const isNotGeneral = !spaceTemplate?.isGeneral;

      return {
        spaceTemplate,
        shouldShowInstitutionLogo: hasLogo && !!isSameInstitution && isNotGeneral,
        institutionLogoUrl: logo,
        institutionName: institution?.name,
      };
    }),
    distinctUntilChanged(
      (a, b) =>
        a.spaceTemplate === b.spaceTemplate &&
        a.shouldShowInstitutionLogo === b.shouldShowInstitutionLogo &&
        a.institutionLogoUrl === b.institutionLogoUrl &&
        a.institutionName === b.institutionName,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private aclService: AclService,
    protected userService: UserService,
    private themeService: ThemeService,
    private notificationService: NotificationToasterService,
    private translateService: TranslateService,
  ) {}

  onEditButton() {
    this.handleUpdateTemplateProps.emit(this.spaceTemplate);
  }

  onCopyCloneUrlButton() {
    if (!this.spaceTemplate?._id) {
      return;
    }
    const cloneUrl = `${window.location.origin}/${URL_CONSTANTS.SPACES}/${URL_CONSTANTS.SPACES_NEW}?templateid=${this.spaceTemplate._id}`;
    navigator.clipboard.writeText(cloneUrl).then(() => {
      this.notificationService.showDefaultSuccessNotification(
        this.translateService.instant('Link copied'),
        this.notificationService.generateNotificationCode(),
      );
    });
  }

  onDeleteButton() {
    this.handleDeleteTemplateClicked.emit(this.spaceTemplate);
  }
}
